<template>
  <div class="text-center">
    <v-btn
      v-if="isGenerating"
      variant="flat"
      size="large"
      prepend-icon="mds:stop-circle"
      class="btn-stop-generating"
      @click="userStopGenerating()"
    >
      Stop generating
    </v-btn>
    <disclaimer-dialog
      ref="disclaimerDialog"
      @conditions-accepted="onConditionsAccepted()"
    />
    <v-dialog v-model="dialog" width="800">
      <v-card>
        <v-toolbar>
          <v-toolbar-title>
            <v-icon class="ml-2">mds:temp-preferences-custom</v-icon>&nbsp;&nbsp;
            <span>Generate workflow with AI</span>
          </v-toolbar-title>
          <help-dialog-workflow-ai
            #default="{ props }"
            content-class="dialog-top-right"
          >
            <v-btn variant="text" icon="mdi-help-circle-outline" v-bind="props" class="mr-2"></v-btn>
          </help-dialog-workflow-ai>
          <v-btn variant="text" icon color="grey" @click="dialog = false">
            <v-icon >mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text>
          <v-form ref="formGenerateDiagram" v-model="validForm">
            <v-container class="flex-wrap pa-0">
              <v-col cols="12" class="pa-0">
                <ai-description-input
                  id="ai-description-input"
                  :text="workflow.aiDescription"
                  @change-description="debouncedSaveAiDescription($event)"
                  label="Describe the business process or the scope of the project"
                  :items="examples"
                  ref="aiDescription"
                  :persistent-hint="true"
                  hint="Which roles are involved in the process? What are the main steps?"
                />
              </v-col>
            </v-container>
          </v-form>
        </v-card-text>
        <v-card-text class="my-0 text-black">
          <v-card class="pa-2 mt-0">
            <v-container class="flex-wrap align-center pb-2">
              <v-col cols="12" class="text-caption font-weight-medium text-uppercase">
                For each step generate cards with AI
                <help-dialog-workflow-cards />
              </v-col>
            </v-container>

            <template v-for="requirementType in requirementTypes" :key="requirementType.id">
              <v-container class="flex-wrap align-center py-2">
                <v-row class="mb-2">
                  <v-col cols="2" class="d-flex justify-center align-center">
                    <v-checkbox
                      :model-value="requirementType.generateWithAI"
                      @update:model-value="saveGenerateCardWithAI(requirementType)"
                      hide-details
                      :disabled="$store.state.isWorkflowDisabled"
                    ></v-checkbox>
                  </v-col>
                  <v-col cols="3" class="d-flex justify-left align-center">
                    <v-chip size="large" variant="elevated" :color="requirementType.color.rgb">
                      <strong>{{requirementType.title}}</strong>
                    </v-chip>
                  </v-col>
                  <v-col cols="7" class="d-flex type-title align-center">
                    {{ requirementType.description }}
                  </v-col>
                </v-row>
                <v-row class="mb-2" v-if="requirementType.triggerType && requirementType.generateWithAI">
                  <v-col cols="5"></v-col>
                  <v-col cols="7" class="d-flex align-center py-0 ml-n3">
                    <v-checkbox
                      v-model="generateCommand"
                      label="Generate Command Attributes with AI"
                      hide-details
                      :disabled="$store.state.isWorkflowDisabled"
                    ></v-checkbox>
                  </v-col>
                </v-row>
                <v-row class="mb-2" v-if="requirementType.queryType && requirementType.generateWithAI">
                  <v-col cols="5"></v-col>
                  <v-col cols="7" class="d-flex align-center py-0 ml-n3">
                    <v-checkbox
                      v-model="generateReadModel"
                      label="Generate Read Model Attributes with AI"
                      hide-details
                      :disabled="$store.state.isWorkflowDisabled"
                    ></v-checkbox>
                  </v-col>
                </v-row>
                <v-row class="mb-2" v-if="requirementType.aggregateType && requirementType.generateWithAI">
                  <v-col cols="5"></v-col>
                  <v-col cols="7" class="d-flex align-center py-0 ml-n3">
                    <v-checkbox
                      v-model="generateEntity"
                      label="Generate Entities with AI"
                      hide-details
                      :disabled="$store.state.isWorkflowDisabled"
                    ></v-checkbox>
                  </v-col>
                </v-row>
              </v-container>
            </template>

          </v-card>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="outlined" size="large" @click="dialog = false">Cancel</v-btn>
          <v-btn variant="flat" size="large" id="test-generate-btn" color="primary" @click="generateDiagramFromText">{{
            "Generate workflow"
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showValidationDialog" max-width="600px">
      <v-card>
        <div class="d-flex justify-space-between pa-2 pb-0">
          <v-card-title>
            <span class="text-h5">Validation issue</span>
          </v-card-title>
          <v-btn variant="text" icon color="grey" @click="showValidationDialog = false">
            <v-icon >mdi-close</v-icon>
          </v-btn>
        </div>
        <v-card-text>{{validationMessage}}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="text" @click="forceInvalidatedAction">Force generation</v-btn>
          <v-btn color="primary" variant="text" @click="openSettingsOrGenerator()">Edit description</v-btn>
          <v-btn color="primary" @click="setSuggestion">{{
            "Use suggestion"
          }}</v-btn>

        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Checks from '@/helpers/checks'
import requirementTypesApi from '@/api/requirementTypesApi'
import workflowApi from '@/api/workflowApi'
import store from '@/store'
import HelpDialogWorkflowAi from '@/components/HelpDialogWorkflowAi'
import HelpDialogWorkflowCards from '@/components/HelpDialogWorkflowCards'
import AiDescriptionInput from '@/components/AiDescriptionInput.vue'
import { EventBus } from '@/event-bus/event-bus.js'
import { mapGetters, mapState } from 'vuex'
import DisclaimerDialog from '@/components/DisclaimerDialog.vue'
import debounce from '@/utils/debounce'
import { scrollToEvent } from '@/utils/scroll'
import { diagramHelpers } from '@/mixins/diagramHelpers'
import { promptHelpers } from '@/api/chatGPT/promptHelpers'
import safeStructuredClone from '@/utils/clone'
import { cardHelpers } from '@/mixins/cardHelpers'
import { workflowHelpers } from '@/mixins/workflowHelpers'
import { createAssistantMessage } from '@/ai/utils/promptUtils'
import { workflowDescriptionExamples } from '@/ai/prompts/common/examples'
import {
  generateSingleCard,
  generateSingleEvent,
  generateCommentReply,
  generateWorkflowCards,
  generateWorkflowDiagram,
  generateWorkflowReadModels,
  validateWorkflowDescription,
  generateWorkflowWriteModels,
  generateEntitiesFromWorkflow
} from '@/ai'

export default {
  props: [
    'bpm',
    'events'
  ],
  components: {
    AiDescriptionInput,
    HelpDialogWorkflowAi,
    HelpDialogWorkflowCards,
    DisclaimerDialog
  },
  methods: {
    openSettingsOrGenerator () {
      this.showValidationDialog = false

      if (this.workflow.eventsJson && this.workflow.eventsJson.length > 0) {
        EventBus.$emit('open-settings', true)
        return
      }

      this.$emit('open-generator')
    },
    openSettings () {
      this.$refs.disclaimerDialog.openDialog()
    },
    onConditionsAccepted () {
      this.dialog = true
    },
    changeSelectedEvent: diagramHelpers.methods.changeSelectedEvent,
    async addReplyToDiagram (input) {
      if (!this.workflow.aiDescription) {
        this.$loadingState.stop()
        store.commit('snackbar/showMessage', { content: 'Please enter a description in settings: Get help from AI to build your workflow', timeout: 6000, color: 'red', location: 'center center' }, { root: true })
        return
      }
      this.$loadingState.start()
      if (!await this.validatePrompt()) {
        this.forcedAction = this.prepareForcedAction(this.addReplyToDiagram, input)
        this.$loadingState.stop()
        return
      }

      const controller = new AbortController()
      this.abortController = controller

      const res = await generateCommentReply({
        workflow: this.workflow,
        svgGraph: this.bpm.svgGraph,
        svgLanes: this.bpm.svgLanes,
        cardTypes: this.requirementTypes,
        input,
        abortController: this.abortController
      })
      this.$loadingState.stop()
      await store.dispatch('workflow/addCardReply', {
        workflowId: input.workflowId,
        requirementId: input.requirementId,
        input: {
          text: res.message,
          generatedByAi: true
        }
      })
    },
    async saveGenerateCardWithAI (requirementType) {
      const payload = {
        generateWithAI: !requirementType.generateWithAI
      }
      await requirementTypesApi.updateRequirementType(this.$route.params.id, requirementType, payload)
    },
    getTextColour (cardType) {
      const res = requirementTypesApi.getTextColour(cardType)
      return res
    },
    userStopGenerating () {
      if (!this.abortController) {
        return
      }

      this.isGenerating = false
      this.$loadingState.stop()
      try {
        this.abortController.abort()
        this.abortController = null
      } catch (e) {
        // Ignore
      }
      store.commit('snackbar/showMessage', { content: 'Generation was stopped by the user', timeout: 3000, color: 'blue' }, { root: true })
    },
    generateDiagramFromText (validateForm = true) {
      if (validateForm && !this.workflow.aiDescription) {
        this.$store.commit('snackbar/showMessage', { content: 'Cannot generate workflow with empty prompt', timeout: 6000, color: 'red', location: 'center center' }, { root: true })
        return
      }

      this.$nextTick(async () => {
        this.$loadingState.start('Validating the query...')
        if (!await this.validatePrompt()) {
          this.forcedAction = this.prepareForcedAction(this.generateDiagramFromText, validateForm)
          return
        }
        this.dialog = false
        this.isGenerating = true
        try {
          const [diagramResponse, diagramMessages] = await this.createDiagram()
          const [cardsResponse, cardsMessages] = await this.addCardsToEvents([
            ...diagramMessages,
            createAssistantMessage(JSON.stringify(diagramResponse))
          ])
          await this.addDataModelsToEvents([...cardsMessages, createAssistantMessage(JSON.stringify(cardsResponse))])
          this.$loadingState.stop()
          this.changeSelectedEvent(null)
        } catch (e) {
          if (e.name !== 'AbortError') {
            console.error(e)
          }
        }
        this.isGenerating = false
        this.$loadingState.stop()
      })
    },
    async forceInvalidatedAction () {
      try {
        this.forceWithoutValidation = true
        this.showValidationDialog = false
        await this.forcedAction()
      } catch (e) {
        console.log(e)
      } finally {
        this.forceWithoutValidation = false
      }
    },
    setSuggestion () {
      this.$nextTick(async () => {
        await this.saveAiDescription(this.suggestedAiDescription)
        this.showValidationDialog = false
        this.openSettingsOrGenerator()
      })
    },
    debouncedSaveAiDescription ({ description, skipDebounce }) {
      if (skipDebounce) {
        this.saveAiDescription(description)
      } else {
        this.saveAiDescriptionDebouncer(description)
      }
    },
    async saveAiDescription (e) {
      await workflowApi.updateWorkflowNoHistoryMutation(
        this.$route.params.projectId,
        {
          id: this.$route.params.id,
          expectedVersion: this.workflow.version,
          aiDescription: e
        }
      )
    },
    addShapeToDiagram (parentId) {
      this.$loadingState.start()
      this.$nextTick(async () => {
        try {
          await this.addShape(parentId, this.bpm, this.requirementTypes)
        } catch (e) {
          console.error(e)
          this.$loadingState.stop()
        }
      })
    },
    addCardToDiagram (eventId, cardTypeId) {
      this.$loadingState.start()
      this.$nextTick(async () => {
        try {
          await this.addCard(eventId, cardTypeId)
        } catch (e) {
          console.error(e)
        } finally {
          this.$loadingState.stop()
        }
      })
    },
    async cancellableRequest (promptFunction, ...args) {
      this.isGenerating = true
      const result = await promptFunction(...args)
      if (this.isGenerating) {
        this.isGenerating = false
        return result
      } else {
        return null
      }
    },
    async addShape (parentId, bpm, requirementTypes) {
      if (!this.workflow.aiDescription) {
        this.$loadingState.stop()
        store.commit('snackbar/showMessage', { content: 'Please enter a description in settings: Get help from AI to build your workflow', timeout: 6000, color: 'red', location: 'center center' }, { root: true })
        return
      }
      this.$loadingState.start()
      if (!await this.validatePrompt()) {
        this.forcedAction = this.prepareForcedAction(this.addShape, parentId, bpm, requirementTypes)
        return
      }

      const controller = new AbortController()
      this.abortController = controller

      const response = await this.cancellableRequest(generateSingleEvent, {
        parentEventId: parentId,
        workflow: this.workflow,
        svgGraph: bpm.svgGraph,
        svgLanes: bpm.svgLanes,
        cardTypes: this.requirementTypes,
        abortController: controller
      })

      const step = response?.event

      if (step) {
        try {
          await this.runStep('create-shape', { type: '1', taskDescription: step.description, laneDescription: step.role })
          for (let i = 0; i < requirementTypes.length; i++) {
            if (requirementTypes[i].generateWithAI) {
              const requirementType = promptHelpers.formatCardKey(requirementTypes[i].title)
              if (step[requirementType]) {
                for (const cardText of step[requirementType]) {
                  await this.runStep('add-card', { cardTypeName: requirementTypes[i].title, cardText })
                }
              }
            }
          }
          await this.addDataModels(step)
          store.commit('snackbar/showMessage', { content: 'Completed', timeout: 3000, color: 'blue' }, { root: true })
        } catch (error) {
          console.error(error)
          store.commit('snackbar/showMessage', { content: 'Error adding', timeout: 6000, color: 'red', location: 'center center' }, { root: true })
        }
      }
      this.$loadingState.stop()
    },
    async addCard (eventId, cardTypeId) {
      if (!this.workflow.aiDescription) {
        this.$loadingState.stop()
        store.commit('snackbar/showMessage', { content: 'Please enter a description in settings: Get help from AI to build your workflow', timeout: 6000, color: 'red', location: 'center center' }, { root: true })
        return
      }
      if (!await this.validatePrompt()) {
        this.forcedAction = this.prepareForcedAction(this.addCard, eventId, cardTypeId)
        return
      }

      const controller = new AbortController()
      this.abortController = controller

      const obj = await this.cancellableRequest(generateSingleCard, {
        eventId,
        workflow: this.workflow,
        svgGraph: this.bpm.svgGraph,
        svgLanes: this.bpm.svgLanes,
        cardTypeId,
        cardTypes: this.requirementTypes,
        abortController: controller
      })
      if (obj) {
        try {
          const index = this.requirementTypes.findIndex(requirementType => requirementType.id === cardTypeId)
          if (promptHelpers.formatCardKey(this.requirementTypes[index].title) === Object.keys(obj)[0]) {
            await this.runStep('add-card', { cardTypeName: this.requirementTypes[index].title, cardText: Object.values(obj)[0], eventId })
          }
          store.commit('snackbar/showMessage', { content: 'Completed', timeout: 3000, color: 'blue' }, { root: true })
        } catch (error) {
          console.error(error)
          store.commit('snackbar/showMessage', { content: 'Error adding', timeout: 6000, color: 'red', location: 'center center' }, { root: true })
        }
      }
      this.$loadingState.stop()
    },
    async createDiagram () {
      if (!this.isGenerating) return
      this.$loadingState.start('Creating events...')
      const controller = new AbortController()
      this.abortController = controller

      const diagramResponse = await generateWorkflowDiagram({
        workflow: this.workflow,
        onChunkReceived: this.addEvents,
        abortController: controller
      })
      if (this.isGenerating) {
        store.commit('snackbar/showMessage', {
          content: 'Added all events',
          timeout: 3000,
          color: 'blue'
        }, { root: true })
      }
      return diagramResponse
    },
    async addCardsToEvents (previousMessages) {
      if (!this.isGenerating) return
      if (!this.requirementTypes.filter(r => r.generateWithAI).length) return
      this.$loadingState.start('Adding cards to events...')
      const controller = new AbortController()
      this.abortController = controller

      const cardsResponse = await generateWorkflowCards({
        workflow: this.workflow,
        cardTypes: this.requirementTypes,
        previousMessages,
        onChunkReceived: this.addCards,
        abortController: controller
      })
      if (this.isGenerating) {
        store.commit('snackbar/showMessage', {
          content: 'Added all corresponding cards',
          timeout: 3000,
          color: 'blue'
        }, { root: true })
      }

      return cardsResponse
    },
    async addDataModelsToEvents (previousMessages) {
      if (!this.isGenerating) return

      let writeModelsResponse
      const commandCardType = diagramHelpers.methods.getCommandCardType(this.requirementTypes)
      if (this.generateCommand && commandCardType?.generateWithAI) {
        this.$loadingState.start('Adding commands to events...')
        const controller = new AbortController()
        this.abortController = controller

        writeModelsResponse = await generateWorkflowWriteModels({
          workflow: this.workflow,
          cardTypes: this.requirementTypes,
          previousMessages,
          onChunkReceived: this.addDataModels,
          abortController: controller
        })
        if (this.isGenerating) {
          store.commit('snackbar/showMessage', {
            content: 'Added all corresponding commands',
            timeout: 3000,
            color: 'blue'
          }, { root: true })
        }
      }

      const readModelCardType = diagramHelpers.methods.getQueryCardType(this.requirementTypes)
      if (this.generateReadModel && readModelCardType?.generateWithAI) {
        this.$loadingState.start('Adding read models to events...')
        const controller = new AbortController()
        this.abortController = controller

        const assistantMessages = writeModelsResponse
          ? [...writeModelsResponse[1], createAssistantMessage(JSON.stringify(writeModelsResponse[0]))]
          : previousMessages

        await generateWorkflowReadModels({
          workflow: this.workflow,
          cardTypes: this.requirementTypes,
          previousMessages: assistantMessages,
          onChunkReceived: this.addReadModels,
          abortController: controller
        })
        if (this.isGenerating) {
          store.commit('snackbar/showMessage', {
            content: 'Added all corresponding read models',
            timeout: 3000,
            color: 'blue'
          }, { root: true })
        }
      }

      const entityCardType = diagramHelpers.methods.getAggregateCardType(this.requirementTypes)
      if (this.generateEntity && entityCardType?.generateWithAI) {
        this.$loadingState.start('Adding entities to workflow...')
        const controller = new AbortController()
        this.abortController = controller

        const res = await generateEntitiesFromWorkflow({
          workflow: this.workflow,
          svgGraph: this.bpm.svgGraph,
          svgLanes: this.bpm.svgLanes,
          cardTypes: this.requirementTypes,
          abortController: controller
        })
        await this.addEntities(res?.entities || [])
        if (this.isGenerating) {
          store.commit('snackbar/showMessage', {
            content: 'Added all corresponding entities',
            timeout: 3000,
            color: 'blue'
          }, { root: true })
        }

        // Link Entities to Aggregate Roots
        await this.$nextTick()
        this.$loadingState.start('Linking entities to events...')
        await this.linkEntities()
        this.$loadingState.stop()
      }
    },
    getEventFromEventObj (obj) {
      const cardLaneId = Object.values(this.bpm.svgLanes).find(lane => lane.name === obj.role).id
      return this.workflow.eventsJson.find(event => event.description === obj.description && event.laneId === cardLaneId)
    },
    updateRouteWithEventId (eventId) {
      this.$router.push({ path: '/workflow/' + this.$route.params.projectId + '/' + this.workflow.id + '/' + eventId }).catch(() => { })
      scrollToEvent(eventId)
    },
    async addEvents (eventObject) {
      await this.runStep('create-shape', {
        type: '1',
        taskDescription: eventObject.description,
        laneDescription: eventObject.role
      })
    },
    async addCards (obj) {
      const event = this.getEventFromEventObj(obj)
      for (let i = 0; i < this.requirementTypes?.length; i++) {
        if (this.requirementTypes[i].generateWithAI) {
          const requirementType = promptHelpers.formatCardKey(this.requirementTypes[i].title)
          if (!Array.isArray(obj[requirementType])) {
            obj[requirementType] = [obj[requirementType]]
          }
          for (const cardText of obj[requirementType]) {
            this.updateRouteWithEventId(event.id)
            await this.runStep('add-card', { cardTypeName: this.requirementTypes[i].title, cardText, eventId: event.id, cardinality: 'one-to-many' })
          }
        }
      }
    },
    async addDataModels (obj) {
      const event = this.getEventFromEventObj(obj)
      const commandCard = cardHelpers.methods.getCommandCard(event)
      const queryCard = cardHelpers.methods.getQueryCards(event)

      if (obj.dataFields?.length > 0) {
        this.updateRouteWithEventId(event.id)
        await this.runStep('add-data-model', {
          example_data: obj.dataFields,
          taskDescription: obj.description,
          laneDescription: obj.role,
          type: obj.type,
          eventId: event.id,
          cardId: obj.cardId || commandCard?.id || null
        })
      }

      if (obj.readModelDataFields?.length > 0) {
        this.updateRouteWithEventId(event.id)
        await this.runStep('add-data-model', {
          example_data: obj.readModelDataFields,
          taskDescription: obj.description,
          laneDescription: obj.role,
          type: 'Read Model',
          eventId: event.id,
          cardId: obj.cardId || queryCard[0]?.id || null
        })
      }
    },
    async addReadModels (obj) {
      const event = this.getEventFromEventObj(obj)
      const readModelCard = cardHelpers.methods.getQueryCards(event)[0]

      if (obj.dataFields?.length > 0) {
        this.updateRouteWithEventId(event.id)
        await this.runStep('add-read-model', {
          example_data: obj.dataFields,
          taskDescription: obj.description,
          laneDescription: obj.role,
          type: obj.type,
          eventId: event.id,
          cardId: obj.cardId || readModelCard?.id || null
        })
      }
    },
    async addEntities (entities) {
      let dataModelsOnWorkflow = safeStructuredClone(this.workflow.dataModels || [])
      let suggestedEntitiesExist = false
      let existingEntitiesCount = 0

      for (const entityFromLLM of entities) {
        const index = (this.workflow.dataModels || []).findIndex(model => model.name === entityFromLLM.name)

        if (index >= 0) {
          existingEntitiesCount++
          continue // Skip if already exists
        }

        const dimensionTable = workflowHelpers.methods.convertLLMResponseToEntity(entityFromLLM)
        dataModelsOnWorkflow = [...dataModelsOnWorkflow, dimensionTable]
      }
      if (existingEntitiesCount === entities.length) {
        suggestedEntitiesExist = true
      }
      if (dataModelsOnWorkflow) {
        await workflowApi.updateDataModels({
          dataModelsOnWorkflow: dataModelsOnWorkflow?.length > 0 ? dataModelsOnWorkflow : undefined,
          workflow: this.workflow
        })

        if (suggestedEntitiesExist) {
          store.commit('snackbar/showMessage', { content: 'All suggested entities already exist!' }, { root: true })
        }
      }
    },
    async linkEntities () {
      const entities = this.workflow.dataModels
      const chunkSize = 5
      const events = this.workflow.eventsJson

      for (let i = 0; i < events.length; i += chunkSize) {
        const chunk = events.slice(i, i + chunkSize)
        const updatedSvgGraph = safeStructuredClone(this.bpm.svgGraph)

        chunk.forEach(event => {
          updatedSvgGraph[event.id].requirementsJson = updatedSvgGraph[event.id].requirementsJson?.map(item => ({
            id: item.id,
            description: item.description,
            releaseNo: item.releaseNo,
            sortkey: item.sortkey,
            cardTypeId: item.cardTypeId,
            done: item.done,
            boundedContext: item.boundedContext,
            creatorId: item.creatorId,
            version: item.version,
            created: item.created,
            entityId: item.cardType.aggregateType
              ? entities.find(entity => entity.name === item.description)?.id
              : null,
            cardinality: item.cardinality
          }))
        })

        await workflowApi.updateWorkflow({
          svgGraph: updatedSvgGraph,
          workflow: this.workflow,
          updateNote: {
            action: 'updated root entity',
            target: ''
          }
        }, true)

        await this.$nextTick()
      }
    },
    async runStep (operation, args = null) {
      const delay = 300
      let oldVersion = this.workflow.version
      switch (operation) {
        case 'create-shape':
          await this.bpm.addShape({
            description: args.taskDescription
          }, {
            laneName: args.laneDescription,
            createNewLaneIfNoLaneId: true
          })
          await this.ensureNextVersion(oldVersion)
          break
        case 'add-card': {
          const index = this.requirementTypes.findIndex(type => type.title === args.cardTypeName)
          if (index >= 0) {
            const cardTypeId = this.requirementTypes[index].id
            const eventId = args.eventId || this.$route.params.eventId
            const res = await workflowApi.createRequirementJson(this.$route.params.id, eventId, {
              cardTypeId,
              description: args.cardText,
              cardinality: args.cardinality
            })
            await this.ensureNextVersion(oldVersion)
            if (res && res.requirements[0].id && cardTypeId === diagramHelpers.methods.getQueryCardType(this.requirementTypes).id) {
              EventBus.$emit('define-read-model-table', { type: 'ai-workflow', cardId: res.requirements[0].id })
            }
          }
          break
        }
        case 'add-data-model': {
          const name = args.taskDescription + ' by ' + args.laneDescription
          const dataFields = []
          for (const example of args.example_data) {
            dataFields.push({
              name: example.fieldName,
              cardinality: example.cardinality,
              tags: example.tags,
              exampleData: example.exampleData
            })
          }
          const eventId = args.eventId || this.$route.params.eventId
          const event = this.workflow?.eventsJson?.find(wt => wt.id === eventId)
          const existingDataModels = event?.dataModels || []

          const dataModels = []
          if (existingDataModels) dataModels.push(...existingDataModels)
          dataModels.push({
            name,
            type: args.type || 'Event Story',
            dataFields,
            cardId: args.cardId
          })
          await workflowApi.updateDataModels({
            eventId,
            dataModelsOnEvent: dataModels,
            workflow: this.workflow
          })
          await this.ensureNextVersion(oldVersion)
          oldVersion = this.workflow.version
          break
        }
        case 'add-read-model': {
          const name = 'Read Model'
          const dataFields = []
          for (const example of args.example_data) {
            dataFields.push({
              name: example.fieldName,
              cardinality: example.cardinality,
              exampleData: example.exampleData
            })
          }
          const eventId = args.eventId || this.$route.params.eventId
          const event = this.workflow?.eventsJson?.find(wt => wt.id === eventId)
          const existingDataModels = event?.dataModels || []

          const dataModels = []
          if (existingDataModels) dataModels.push(...existingDataModels)
          dataModels.push({
            name,
            type: args.type || 'Read Model',
            dataFields,
            cardId: args.cardId
          })
          await workflowApi.updateDataModels({
            eventId,
            dataModelsOnEvent: dataModels,
            workflow: this.workflow
          })
          await this.ensureNextVersion(oldVersion)
          oldVersion = this.workflow.version
          break
        }
      }
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          const { errors, errorList } = Checks.checkDiagramConsistency(this.bpm.svgGraph, this.bpm.svgLanes, this.bpm.svgGroups, this.bpm.events)
          if (errors === 0) {
            resolve()
          } else {
            this.testResults = errorList
            reject(new Error('Failed with ' + errors + ' failed tests. ' + errorList.toString()))
          }
        }, delay)
      })
    },
    async ensureNextVersion (oldVersion) {
      while (oldVersion === this.workflow.version) {
        await this.wait100ms()
      }
    },
    async wait100ms () {
      return new Promise((resolve) => {
        setTimeout(() => { resolve() }, 100)
      })
    },
    async validatePrompt () {
      if (this.forceWithoutValidation === false) {
        const res = await validateWorkflowDescription({ workflow: this.workflow })
        if (res?.answer === 'yes') {
          return true
        } else if (res?.answer === 'no') {
          this.$loadingState.stop()
          this.showValidationDialog = true
          let message = 'Sorry, can\'t understand the description: "' + this.workflow.aiDescription + '".'
          if (res['did you mean']) {
            message += ' Did you mean: "' + res['did you mean'] + '"' + '?'
          }
          this.suggestedAiDescription = res['did you mean']
          this.validationMessage = message
          return false
        } else {
          return false
        }
      }
      return true
    },
    prepareForcedAction (fn, ...args) {
      // Prepares the function to be triggered on Force Generate button
      // Since prompt validation is used in multiple places, we need to dynamically pick which ChatGPT action we want to
      // trigger. Because some of them have different function parameters, this is the JS way of preparing such
      // functions to call them later
      return async function () {
        return await fn(...args)
      }
    }
  },
  data () {
    return {
      validForm: true,
      dialog: false,
      testResults: [],
      chatLimitRoleLength: 'Keep the role name to max 16 characters.',
      chatLimitDescriptionLength: 'Keep the description to one sentence and max 40 characters.',
      suggestedAiDescription: '',
      forceWithoutValidation: false,
      showValidationDialog: false,
      validationMessage: '',
      forcedAction: () => {},
      saveAiDescriptionDebouncer: debounce(e => { this.saveAiDescription(e) }, 1000),
      isGenerating: false,
      chat: null,
      generateCommand: true,
      generateReadModel: true,
      generateEntity: true,
      abortController: null
    }
  },
  computed: {
    ...mapState({
      workflow: state => state.workflow.workflow,
      requirementTypes: state => state.requirementTypes.requirementTypes
    }),
    examples () {
      return workflowDescriptionExamples
    },
    ...mapGetters({
      loadingState: 'loading/getLoadingState'
    })
  },
  watch: {
    isGenerating (newVal) {
      if (!newVal && this.chat) {
        this.chat.stopStreaming()
      }
    }
  },
  mounted () {
    EventBus.$on('generate-diagram', async (workflowId) => {
      if (!this.loadingState && (this.workflow.id === this.$route.params.id)) {
        this.$loadingState.start()
        this.generateDiagramFromText(false)
      }
    })
    EventBus.$on('add-card-with-ai', async (eventId, cardTypeId) => {
      this.addCardToDiagram(eventId, cardTypeId)
    })
  },
  beforeUnmount () {
    EventBus.$off('generate-diagram')
    EventBus.$off('add-card-with-ai')
  }
}
</script>

<style>
.generate-button {
  z-index: 1;
  padding-left: 10px;
  padding-right: 10px;
  text-transform: none !important;
}
</style>

<style scoped>
.btn-stop-generating {
  position: fixed;
  bottom: 64px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000000;
}
</style>
